import { useCallback, useMemo } from 'react';

import { useI18n } from '@change-corgi/core/react/i18n';
import { useTracking } from '@change-corgi/core/react/tracking';
import { useUtilityContext } from '@change-corgi/core/react/utilityContext';

import type { HitProps } from '../../types';

type Result = ModelHookResult<
	{
		createdAtLocalized: string;
		petitionUrl: string;
		imageUrl: string;
		signaturesCount: number;
		signaturesCountLocalized: string;
		locationName: string | null;
	},
	{
		trackPetitionCardClick: () => void;
	}
>;

export function useHitCard({ hit }: HitProps): Result {
	const { getCountry, localizeNumber, localizeDate } = useI18n();
	const { environment } = useUtilityContext();
	const track = useTracking();

	const imageHost = useMemo(() => {
		if (environment.getEnvironment() === 'production') {
			return 'https://assets.change.org/';
		}
		return 'https://assets.staging-change.org/';
	}, [environment]);

	const trackPetitionCardClick = useCallback(() => {
		void track('search_result_petition_card_click');
	}, [track]);

	return {
		data: {
			createdAtLocalized: useMemo(
				// The typesense server returns the created_at in UNIX timestamp value, so we need to multiply by 1000 to get milliseconds
				() => localizeDate(new Date(hit.created_at * 1000), 'date', 'long'),
				[hit.created_at, localizeDate],
			),
			petitionUrl: `/p/${encodeURIComponent(hit.slug)}?source_location=search`,
			imageUrl: useMemo(() => {
				const transformedPath = hit.photo_url; // ?.replace('-fullsize.jpg', '-400x400-noPad.jpg');

				return transformedPath
					? `${imageHost}${transformedPath}`
					: 'https://static.change.org/images/default-petition-darker.gif';
			}, [hit.photo_url, imageHost]),
			signaturesCount: hit.total_signature_count,
			signaturesCountLocalized: useMemo(
				() => localizeNumber(hit.total_signature_count),
				[hit.total_signature_count, localizeNumber],
			),
			locationName: useMemo(() => {
				const country = getCountry(hit.location?.country_code || 'US');

				if (hit.location?.city && country) {
					return `${hit.location.city}, ${country.name}`;
				}
				if (hit.location?.city) {
					return hit.location.city;
				}
				if (country) {
					return country.name;
				}
				return null;
			}, [getCountry, hit.location]),
		},
		actions: {
			trackPetitionCardClick,
		},
	};
}
